/**
 * In-app user manual — ordered table of contents.
 *
 * Each section: { domain, title, audience, pages: [{ slug, title, draft? }] }
 *   - `domain`   maps to the folder under src/help/<domain>/ that holds the .md files
 *   - `audience` is which portal's manual this section belongs to. Today only the
 *     admin manual exists; customer/vendor manuals will be added as separate
 *     audiences later. visibleManifest() filters on this.
 *   - `draft: true` on a page keeps it out of the sidebar/search/pager but still
 *     reachable by direct URL (used while a page is being written).
 *
 * Do NOT hand-write page bodies here — one .md file per page under src/help/.
 */

export const ADMIN = "admin";

const sections = [
  {
    domain: "getting-started",
    title: "Getting started",
    audience: ADMIN,
    pages: [
      { slug: "overview", title: "How the system is organised" },
      { slug: "logging-in", title: "Logging in and your profile" },
      { slug: "dashboard", title: "Reading the Dashboard" },
      { slug: "using-lists", title: "Working with lists, filters and exports" },
    ],
  },
  {
    domain: "delivery-advice",
    title: "Delivery Advices",
    audience: ADMIN,
    pages: [
      { slug: "overview", title: "Delivery Advices and their lifecycle" },
      { slug: "create-credit-da", title: "Creating a Credit DA" },
      { slug: "create-cash-da", title: "Creating a Cash DA" },
      { slug: "charges-and-costs", title: "DA charges and costs" },
      { slug: "milestones", title: "DA milestones" },
    ],
  },
  {
    domain: "trip-scheduling",
    title: "Trip Scheduling",
    audience: ADMIN,
    pages: [
      { slug: "overview", title: "Trip Scheduling (New) overview" },
      { slug: "build-a-trip", title: "Building a trip and allocating assets" },
      { slug: "leg-lifecycle", title: "The life of a leg" },
      { slug: "container-view", title: "Container-centric view" },
      { slug: "bulk-actions", title: "Bulk actions on legs" },
    ],
  },
  {
    domain: "sub-hire",
    title: "Sub Hire",
    audience: ADMIN,
    pages: [
      { slug: "overview", title: "Sub Hire and its six stages" },
      { slug: "approve-reject", title: "Approving and rejecting sub-hire jobs" },
      { slug: "commitment", title: "Sub Hire Commitment" },
    ],
  },
  {
    domain: "emergency",
    title: "Emergency operations",
    audience: ADMIN,
    pages: [
      { slug: "overview", title: "How emergency operations work" },
      { slug: "status-and-validations", title: "Status flow and validations" },
      { slug: "order-containers", title: "Emergency Order Containers" },
      { slug: "emergency-trips", title: "Emergency Trips" },
      { slug: "vendor-bulk-uploads", title: "Vendor Bulk Uploads" },
      { slug: "vendor-draft-jobs", title: "Reviewing vendor draft jobs" },
      { slug: "workload", title: "Workload" },
    ],
  },
  {
    domain: "control-tower",
    title: "Control Tower",
    audience: ADMIN,
    pages: [
      { slug: "overview", title: "Control Tower live tracking" },
      { slug: "management-dashboard", title: "Management Dashboard" },
    ],
  },
  {
    domain: "invoicing",
    title: "Invoicing and payments",
    audience: ADMIN,
    pages: [
      { slug: "customer-invoices", title: "Customer Invoices" },
      { slug: "credit-notes", title: "Credit Notes" },
      { slug: "debit-notes", title: "Debit Notes" },
      { slug: "bulk-invoice-logs", title: "Bulk Invoice Logs" },
      { slug: "payment-receipts", title: "Payment Receipts" },
    ],
  },
  {
    domain: "customers",
    title: "Customers",
    audience: ADMIN,
    pages: [
      { slug: "overview", title: "Customers overview and lifecycle" },
      { slug: "customer-record", title: "The customer record, field by field" },
      { slug: "customer-tariff", title: "Customer Tariff, field by field" },
      { slug: "quotations", title: "Quotations" },
    ],
  },
  {
    domain: "vendors",
    title: "Vendors",
    audience: ADMIN,
    pages: [
      { slug: "overview", title: "Vendors overview and lifecycle" },
      { slug: "vendor-record", title: "The vendor record, field by field" },
      { slug: "vendor-tariff", title: "Vendor Tariff, field by field" },
      { slug: "new-company-requests", title: "New Company Requests" },
    ],
  },
  {
    domain: "assets",
    title: "Assets and fleet",
    audience: ADMIN,
    pages: [
      { slug: "overview", title: "How drivers, vehicles and trailers fit together" },
      { slug: "vehicles", title: "Vehicles" },
      { slug: "trailers", title: "Trailers" },
      { slug: "drivers", title: "Drivers" },
      { slug: "maintenance", title: "Asset Maintenance" },
      { slug: "availability", title: "Asset Availability" },
      { slug: "vehicle-cost", title: "Vehicle Cost" },
      { slug: "gps-profile", title: "GPS Profile" },
    ],
  },
  {
    domain: "integration",
    title: "Integrations",
    audience: ADMIN,
    pages: [
      { slug: "overview", title: "How the system connects to other systems" },
      { slug: "inbound", title: "Inbound Integration Logs" },
      { slug: "outbound", title: "Outbound Integration Logs" },
      { slug: "troubleshooting", title: "When something does not sync" },
    ],
  },
  {
    domain: "bonus",
    title: "Bonus",
    audience: ADMIN,
    pages: [
      { slug: "how-bonus-works", title: "How bonus works" },
      { slug: "route-bonus", title: "Setting bonus on a route" },
      { slug: "leg-bonus", title: "Adjusting bonus on a leg" },
      { slug: "lease-driver-bonus", title: "Lease Driver bonus" },
      { slug: "sap-bonus-payout", title: "SAP Bonus Payout and approval" },
      { slug: "processing-log", title: "Driver bonus processing log" },
    ],
  },
  {
    domain: "reports",
    title: "Reports",
    audience: ADMIN,
    pages: [
      { slug: "reports", title: "Reports" },
      { slug: "reports-center", title: "Reports Center" },
    ],
  },
  {
    domain: "reference",
    title: "Reference",
    audience: ADMIN,
    pages: [
      { slug: "master-data", title: "Master data and configuration" },
      { slug: "glossary", title: "Glossary" },
    ],
  },
];

export default sections;

/* ------------------------------------------------------------------ */
/* Audience helpers                                                     */
/* ------------------------------------------------------------------ */

/**
 * Which audience the current viewer belongs to. Read from the login
 * response stored in localStorage. Wrapped so a missing/corrupt value
 * never throws — an unknown viewer simply sees nothing.
 */
export function currentAudience() {
  try {
    const t = (localStorage.getItem("user_type") || "").toLowerCase();
    if (t === "admin") return ADMIN;
    return t || null;
  } catch (e) {
    return null;
  }
}

export function isAdminViewer() {
  return currentAudience() === ADMIN;
}

/** Sections visible to the current viewer. */
export function visibleManifest() {
  const aud = currentAudience();
  return sections.filter((s) => s.audience === aud);
}

/** Flat list of { domain, slug, title, sectionTitle } for the visible manifest. */
export function flatPages() {
  const out = [];
  visibleManifest().forEach((s) => {
    s.pages
      .filter((p) => !p.draft)
      .forEach((p) => {
        out.push({
          domain: s.domain,
          slug: p.slug,
          title: p.title,
          sectionTitle: s.title,
        });
      });
  });
  return out;
}

export function findPage(domain, slug) {
  const section = visibleManifest().find((s) => s.domain === domain);
  if (!section) return null;
  const idx = section.pages.findIndex((p) => p.slug === slug);
  if (idx === -1) return null;
  return {
    section,
    page: section.pages[idx],
    prev: section.pages[idx - 1] || null,
    next: section.pages[idx + 1] || null,
  };
}

export function firstPage() {
  const flat = flatPages();
  return flat.length ? flat[0] : null;
}

/** Can the current viewer open this page at all? Used by the route guard. */
export function canViewPage(domain, slug) {
  return !!findPage(domain, slug);
}
